/**
 * @file TodaysDeliveriesWidget.js
 * @version 1.8.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-10-28
 *
 * 1.8.0 (2026-10-28, Cam — rep mobile review): on a rep's phone each
 * delivery is two lines — time and customer on top, then product ·
 * quantity · lorry — instead of a five-column table whose Transport heading
 * was cut off at the right edge. The desktop table is unchanged.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ContactPopover from '../components/ContactPopover';
import { API_URL } from '../config';
import RepRefreshButton from './RepRefreshButton';
import { formatTonnes } from '../utils/numberFormatters';
import useRepMobile from '../hooks/useRepMobile';
import './RepWidgets.css';

/*
 * Read from Transport Planning (the bulk board only) through the
 * server, which narrows the rows to a rep's own customers. Cut over from the
 * Google deliveries sheet on 2026-10-01 — the rows keep the sheet's keys.
 */

// Reduce a rep's full name to initials ("Matt Jackson" -> "MJ").
const initialiseRep = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts.map((p) => p[0].toUpperCase()).join('');
};

// Known mill short codes; anything else falls back to its first 3 letters.
const MILL_ABBREVIATIONS = {
  risdon: 'RIS',
  lifton: 'LIF',
  forfarmers: 'FF',
};

const abbreviateMill = (name) => {
  if (!name) return '';
  const key = String(name).trim().toLowerCase().replace(/\s+/g, '');
  if (MILL_ABBREVIATIONS[key]) return MILL_ABBREVIATIONS[key];
  const up = String(name).trim().toUpperCase();
  return up.length <= 4 ? up : up.replace(/[^A-Z0-9]/g, '').slice(0, 3);
};

function TodaysDeliveriesWidget({
  settings = {},
  onSettingsChange,
  heightStyle,
  isRep = false
}) {
  // A rep's phone: two-line rows instead of the table (1.8.0).
  const repMobile = useRepMobile();
  const [deliveries, setDeliveries] = useState([]);
  // When the board was last read, and whether that worked. A failed read keeps
  // the rows already on screen, so this is what tells them apart.
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(settings.zoom || 12);
  const [popover, setPopover] = useState(null); // { name, type, anchorEl }

  // Transport plan through the day, so the card follows them: re-read every
  // minute, quietly (no spinner over rows that are probably unchanged), and at
  // once when the tab is shown again (Cam, 19 Oct — last-minute and on-the-day
  // changes; a laptop waking up should not show the morning's plan).
  useEffect(() => {
    fetchDeliveries();
    const timer = setInterval(() => fetchDeliveries(false, true), 60000);
    let shownAt = 0;
    const onShow = () => {
      // Focus and visibility often fire together: one read for both.
      if (document.hidden || Date.now() - shownAt < 5000) return;
      shownAt = Date.now();
      fetchDeliveries(false, true);
    };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, []);

  // Update zoom when settings change
  useEffect(() => {
    if (settings.zoom !== undefined && settings.zoom !== zoom) {
      setZoom(settings.zoom);
    }
  }, [settings.zoom]);

  const fetchDeliveries = async (force = false, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/todays-deliveries${force ? '?refresh=1' : ''}`, {
        credentials: 'include',
        cache: 'no-store'
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveries(Array.isArray(data.rows) ? data.rows : []);
        setSyncedAt(data.syncedAt || null);
        setSyncError(data.lastError || null);
      } else {
        setSyncError(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
      setSyncError(error.message);
    }
    setLoading(false);
  };

  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
    if (onSettingsChange) {
      onSettingsChange({ ...settings, zoom: newZoom });
    }
  };

  const handleNameClick = (e, name, type) => {
    if (!name) return;
    e.stopPropagation();
    setPopover({ name, type, anchorEl: e.currentTarget });
  };

  // Format time for deliveries
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const match = String(timeStr).match(/(\d{1,2}):(\d{2})/);
    if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
    return String(timeStr);
  };

  const formatQty = (qty) => {
    if (!qty) return '-';
    const num = parseFloat(qty);
    if (isNaN(num)) return qty;
    // Capital T, always — the project's rule for any tonnage shown to a user.
    return formatTonnes(num);
  };

  // Sort by haulier then time (matching tv-dashboard)
  const sortedDeliveries = [...deliveries].sort((a, b) => {
    const haulierA = a['Haulier'] || '';
    const haulierB = b['Haulier'] || '';
    if (!haulierA && haulierB) return 1;
    if (haulierA && !haulierB) return -1;
    const haulierCompare = haulierA.localeCompare(haulierB);
    if (haulierCompare !== 0) return haulierCompare;
    // Then by lorry: our own fleet is one haulier, so without this every
    // vehicle's run is shuffled together by time.
    const vehicleCompare = String(a['Vehicle'] || '').localeCompare(String(b['Vehicle'] || ''));
    if (vehicleCompare !== 0) return vehicleCompare;

    const timeA = a['Time'], timeB = b['Time'];
    if (!timeA && !timeB) return 0;
    if (!timeA) return 1;
    if (!timeB) return -1;
    return String(timeA).localeCompare(String(timeB));
  });

  return (
    <div className="dashboard-card deliveries-card" style={heightStyle}>
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">local_shipping</span>
          Today's Deliveries
          {/* Where the data comes from is an operator's question, not a rep's —
              they cannot act on the answer either way. */}
          {!isRep && (
            <span
              className="material-symbols-rounded data-source-icon"
              title={syncError
                ? `Could not read Transport Planning (${syncError})`
                : `Live from Transport Planning${syncedAt ? ` — read ${new Date(syncedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}`}
            >
              {syncError ? 'cloud_alert' : 'cloud_done'}
            </span>
          )}
        </h3>
        <div className="header-controls">
          {/* The count is already the length of the list directly beneath it. */}
          {!isRep && <span className="header-badge">{deliveries.length} scheduled</span>}
          {isRep && (
            <RepRefreshButton
              cachedAt={syncedAt}
              loading={loading}
              onRefresh={() => fetchDeliveries(true)}
            />
          )}
          <div className="zoom-controls">
            <button
              className="zoom-btn"
              onClick={() => handleZoomChange(Math.max(9, zoom - 1))}
            >−</button>
            <button
              className="zoom-btn"
              onClick={() => handleZoomChange(Math.min(18, zoom + 1))}
            >+</button>
          </div>
        </div>
      </div>
      <div className="card-body scrollable-body">
        {loading ? (
          <LoadingSpinner />
        ) : sortedDeliveries.length === 0 ? (
          <div className="empty-state">No deliveries scheduled</div>
        ) : (
          repMobile ? (
            <ul className="rep-deliveries">
              {sortedDeliveries.map((d, idx) => {
                const lorry = d['CustomerCollection'] ? 'Customer collects' : (d['Vehicle'] || d['Haulier'] || '');
                return (
                  <li key={idx} className="rep-delivery">
                    <div className="rep-delivery-top">
                      <span className="rep-delivery-time">{formatTime(d['Time']) || '—'}</span>
                      <button
                        type="button"
                        data-read-safe
                        className="rep-delivery-customer"
                        onClick={(e) => handleNameClick(e, d['Customer'], 'customer')}
                      >{d['Customer']}</button>
                    </div>
                    <div className="rep-delivery-sub">
                      {[d['Product'], formatQty(d['Quantity']), lorry].filter(Boolean).join(' · ')}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
          <div className="deliveries-table" style={{ fontSize: zoom }}>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Customer</th>
                  <th className='rep'>Rep</th>
                  <th>Product</th>
                  <th className="col-mill">Mill</th>
                  <th className="col-qty">Qty</th>
                  <th style={{ textAlign: "center" }}>Transport</th>
                </tr>
              </thead>
              <tbody>
                {sortedDeliveries.map((d, idx) => (
                  <tr key={idx}>
                    <td className="col-time">{formatTime(d['Time'])}</td>
                    <td>
                      <span
                        className="clickable-name"
                        onClick={(e) => handleNameClick(e, d['Customer'], 'customer')}
                      >{d['Customer']}</span>
                    </td>
                    <td className='rep'>
                      {d['Rep'] ? (
                        <span
                          className="clickable-name"
                          onClick={(e) => handleNameClick(e, d['Rep'], 'rep')}
                        >{initialiseRep(d['Rep'])}</span>
                      ) : ''}
                    </td>
                    <td>{d['Product']}</td>
                    <td className="col-mill">{abbreviateMill(d['Mill'])}</td>
                    <td className="col-qty">{formatQty(d['Quantity'])}</td>
                    <td>
                      <div className='transport'>
                        {d['CustomerCollection'] ? (
                          // The customer collects from the mill: no haulier of ours.
                          <span className="collection-badge" title="The customer collects from the mill">Customer collects</span>
                        ) : d['Haulier'] ? (
                          <span
                            className="clickable-name"
                            onClick={(e) => handleNameClick(e, d['Haulier'], 'haulier')}
                          >{d['Haulier']}</span>
                        ) : ''}
                        {d['Vehicle'] && <span className='vehicle'>{d['Vehicle']}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>
      {/* Contact popover - portalled to body */}
      {popover && ReactDOM.createPortal(
        <ContactPopover
          name={popover.name}
          type={popover.type}
          anchorEl={popover.anchorEl}
          onClose={() => setPopover(null)}
        />,
        document.body
      )}
    </div>
  );
}

export default TodaysDeliveriesWidget;
